/**
 * AppShell — layout principal de las rutas autenticadas.
 *
 * Estructura:
 *   ┌────────────────────────────────────────────┐
 *   │ Sidebar │ Header                            │
 *   │         ├───────────────────────────────────│
 *   │         │ <Outlet/> contenido de la ruta    │
 *   │         │                                   │
 *   └────────────────────────────────────────────┘
 */

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { IntegridadBanner } from './IntegridadBanner';
import { GlobalProgressBar } from './GlobalProgressBar';
import { CommandPalette } from './CommandPalette';
import { useSessionTimeout } from '@/features/auth/use-session-timeout';
import { useMultiTabSessionSync } from '@/features/auth/use-multi-tab-session-sync';

export function AppShell() {
  // Auto-logout por inactividad (GAMP 5 §11.10(d)). Activo solo si hay usuario.
  useSessionTimeout();
  // Multi-tab sync: si en otra tab se hace logout, esta también se cierra.
  useMultiTabSessionSync();

  // Page transition: el `key` cambia con el pathname, lo que fuerza remount
  // del wrapper y re-dispara la animación fade-in (200ms ease estándar).
  // Definido en globals.css (.page-transition). Respeta prefers-reduced-motion.
  const location = useLocation();

  // Command Palette global (paquete AD). Atajo Cmd+K (Mac) / Ctrl+K (Win/Linux).
  // El Header dispara un CustomEvent 'open-command-palette' que también
  // abre la palette, así un botón visible puede invitar al power-user.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (!isCmdK) return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    const onOpenEvent = () => setPaletteOpen(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('open-command-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('open-command-palette', onOpenEvent);
    };
  }, []);

  // Cerrar el palette automáticamente al cambiar de ruta (paquete AD). Sin
  // esto, navegar a través del palette lo deja "abierto" virtualmente
  // (el dialog se cierra pero el state quedaba en true por un tick).
  useEffect(() => {
    setPaletteOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Skip-to-content link — invisible hasta Tab desde el top. Permite a
          usuarios de keyboard / screen reader saltarse sidebar y header
          (paquete AI, WCAG 2.4.1 "Bypass Blocks"). */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido
      </a>
      <GlobalProgressBar />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <IntegridadBanner />
        {/* Canvas institucional beige (brandbook §"Backgrounds & imagery"). */}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto bg-everex-beige p-8"
        >
          <div key={location.pathname} className="page-transition">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
